import type {
  Pipeline,
  Step,
  StepParams,
  StepType,
  PreviewBlock,
  ColorSeparateParams,
  GradientSepParams,
} from './types';
import {
  DEFAULT_HALFTONE_PARAMS,
  DEFAULT_PREVIEW,
  DEFAULT_GREYSCALE_PARAMS,
} from './types';
import {
  allowedChildren,
  isLeafProducing,
  singleChild,
} from './validity';
import {
  defaultNameForType,
  disambiguate,
  siblingNamesOf,
} from './naming';

// Tree manipulation helpers — pure functions over Pipeline.
// Mutate the passed pipeline in place; callers wrap in a runes-aware setter
// so reactivity fires.

let _idCounter = 0;
export function newStepId(): string {
  _idCounter += 1;
  return `s${_idCounter.toString().padStart(3, '0')}`;
}

// Reset the id counter (testing convenience).
export function _resetIdCounter(): void {
  _idCounter = 0;
}

// Bump the internal id counter past `n` so that subsequent `newStepId()`
// calls produce ids greater than the supplied value. Used after applying a
// recipe whose steps were generated in another session — keeps any new
// step ids unique.
export function bumpIdCounterAtLeast(n: number): void {
  if (n > _idCounter) _idCounter = n;
}

export function findStep(pipeline: Pipeline, id: string): Step | undefined {
  return pipeline.steps[id];
}

export function findParentId(
  pipeline: Pipeline,
  childId: string,
): string | undefined {
  for (const [id, step] of Object.entries(pipeline.steps)) {
    if (step.children.includes(childId)) return id;
  }
  return undefined;
}

export function walkPostOrder(pipeline: Pipeline): Step[] {
  const out: Step[] = [];
  function visit(id: string): void {
    const step = pipeline.steps[id];
    if (!step) return;
    for (const c of step.children) visit(c);
    out.push(step);
  }
  visit(pipeline.root_id);
  return out;
}

export function walkLeaves(pipeline: Pipeline): Step[] {
  // A "leaf" in the export sense = step with no children AND a leaf-producing
  // type. `channel` / `band` with no child are not exportable.
  const leaves: Step[] = [];
  for (const step of walkPostOrder(pipeline)) {
    if (step.children.length === 0 && isLeafProducing(step.type)) {
      leaves.push(step);
    }
  }
  return leaves;
}

function emptyParamsFor(type: StepType): StepParams {
  switch (type) {
    case 'input':
      return { rotation: 0 };
    case 'greyscale':
      return { ...DEFAULT_GREYSCALE_PARAMS };
    case 'color_separate':
      return {
        channels: [
          { name: 'Red', target: '#FF0000', threshold: 0 },
          { name: 'Blue', target: '#0000FF', threshold: 0 },
          { name: 'Yellow', target: '#FFFF00', threshold: 0 },
        ],
      };
    case 'channel':
      return { channel_index: 0 };
    case 'gradient_sep':
      return {
        bands: [
          { name: 'Band 1', min: 0.0, max: 0.34 },
          { name: 'Band 2', min: 0.34, max: 0.67 },
          { name: 'Band 3', min: 0.67, max: 1.0 },
        ],
      };
    case 'band':
      return { band_index: 0 };
    case 'halftone':
      return { ...DEFAULT_HALFTONE_PARAMS };
  }
}

function makeStep(
  type: StepType,
  name: string,
  params: StepParams,
  preview?: PreviewBlock,
): Step {
  return {
    id: newStepId(),
    type,
    name,
    params,
    children: [],
    ...(preview ? { preview: { ...preview } } : {}),
  };
}

export function createEmptyPipeline(inputName: string): Pipeline {
  const input = makeStep('input', inputName, emptyParamsFor('input'));
  return {
    root_id: input.id,
    steps: { [input.id]: input },
  };
}

// Add a new step as a child of `parentId`. Returns the new step's id.
// Enforces the validity matrix and single-child rule.
export function addChild(
  pipeline: Pipeline,
  parentId: string,
  childType: StepType,
  options: {
    name?: string;
    params?: StepParams;
    inheritNameFromParent?: boolean;
  } = {},
): string {
  const parent = pipeline.steps[parentId];
  if (!parent) {
    throw new Error(`addChild: parent ${parentId} not found`);
  }

  // Virtual nodes (channel/band) are synced from parent params, not added
  // through this entry point.
  if (childType === 'channel' || childType === 'band') {
    throw new Error(
      `addChild: ${childType} children are synced from parent params, not added directly`,
    );
  }

  if (!allowedChildren(parent.type).includes(childType)) {
    throw new Error(
      `addChild: type ${childType} is not allowed under ${parent.type}`,
    );
  }

  if (singleChild(parent.type) && parent.children.length >= 1) {
    throw new Error(
      `addChild: ${parent.type} accepts at most one child`,
    );
  }

  const baseName =
    options.name ??
    (options.inheritNameFromParent ? parent.name : defaultNameForType(childType));
  const finalName = disambiguate(baseName, siblingNamesOf(pipeline, parentId));

  const params = options.params ?? emptyParamsFor(childType);

  const preview =
    childType === 'halftone' ? { ...DEFAULT_PREVIEW } : undefined;

  const step = makeStep(childType, finalName, params, preview);
  pipeline.steps[step.id] = step;
  parent.children.push(step.id);
  return step.id;
}

// Delete a step and its entire subtree. The root cannot be deleted.
// Returns the ids of all removed steps (for cache invalidation).
export function deleteStep(pipeline: Pipeline, stepId: string): string[] {
  if (stepId === pipeline.root_id) {
    throw new Error('deleteStep: cannot delete the root input step');
  }
  const parentId = findParentId(pipeline, stepId);
  if (!parentId) return [];

  // Collect the entire subtree, including virtual children (channel/band)
  // that exist as real Step records under fan-out parents.
  const removed: string[] = [];
  function collect(id: string): void {
    const s = pipeline.steps[id];
    if (!s) return;
    for (const c of [...s.children]) collect(c);
    removed.push(id);
  }
  collect(stepId);

  // Detach from parent's children list.
  const parent = pipeline.steps[parentId];
  if (parent) {
    parent.children = parent.children.filter((id) => id !== stepId);
  }

  for (const id of removed) {
    delete pipeline.steps[id];
  }

  return removed;
}

export function renameStep(
  pipeline: Pipeline,
  stepId: string,
  newName: string,
): void {
  const step = pipeline.steps[stepId];
  if (!step) return;
  step.name = newName;
}

export function replaceStepParams(
  pipeline: Pipeline,
  stepId: string,
  params: StepParams,
): void {
  const step = pipeline.steps[stepId];
  if (!step) return;
  step.params = params;
}

export function setStepPreview(
  pipeline: Pipeline,
  stepId: string,
  preview: PreviewBlock,
): void {
  const step = pipeline.steps[stepId];
  if (!step) return;
  step.preview = { ...preview };
}

export function isExportableLeaf(step: Step): boolean {
  return step.children.length === 0 && isLeafProducing(step.type);
}

// Type guards / narrowed param accessors used by sync logic.
export function asColorSeparateParams(
  step: Step,
): ColorSeparateParams | undefined {
  if (step.type !== 'color_separate') return undefined;
  return step.params as ColorSeparateParams;
}

export function asGradientSepParams(
  step: Step,
): GradientSepParams | undefined {
  if (step.type !== 'gradient_sep') return undefined;
  return step.params as GradientSepParams;
}
